class LayoutRadialGradient {
  constructor(options = {}) {
    this.gradientSelector =
      options.gradientSelector || '[data-layout-radial-gradient]';
    this.mainContentSelector =
      options.mainContentSelector || '#MainContent';
    this.headerSelector = options.headerSelector || '#page-header';
    this.announcementSelector =
      options.announcementSelector || '#announcement-bar';
    this.firstSectionRatio =
      typeof options.firstSectionRatio === 'number'
        ? options.firstSectionRatio
        : 0.7;
    this.debounceMs = options.debounceMs ?? 100;

    this.gradient = document.querySelector(this.gradientSelector);
    this.resizeObserver = null;
    this.resizeTimer = null;
    this.rafId = null;

    this.scheduleUpdate = this.scheduleUpdate.bind(this);
    this.update = this.update.bind(this);
  }

  static visibleHeight(el) {
    if (!el) return 0;
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return 0;
    return el.getBoundingClientRect().height || 0;
  }

  getFirstMainSection() {
    const sections = document.querySelectorAll(
      `${this.mainContentSelector} .shopify-section`
    );
    for (const section of sections) {
      if (LayoutRadialGradient.visibleHeight(section) > 0) return section;
    }
    return null;
  }

  update() {
    if (!this.gradient) return;

    const header = document.querySelector(this.headerSelector);
    const announcement = document.querySelector(this.announcementSelector);
    const firstSection = this.getFirstMainSection();

    // Read phase: query all geometry together.
    const headerH = LayoutRadialGradient.visibleHeight(header);
    const announcementH = LayoutRadialGradient.visibleHeight(announcement);
    const firstSectionH = LayoutRadialGradient.visibleHeight(firstSection);
    const height =
      announcementH + headerH + firstSectionH * this.firstSectionRatio;

    // Write phase: defer the style mutation to the next frame so we never
    // write-then-read in the same task (avoids forced reflow / layout thrash).
    if (this.rafId) window.cancelAnimationFrame(this.rafId);
    this.rafId = window.requestAnimationFrame(() => {
      this.gradient.style.setProperty(
        '--layout-radial-height',
        `${Math.max(0, height)}px`
      );
    });
  }

  scheduleUpdate() {
    window.clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(this.update, this.debounceMs);
  }

  bindObservers() {
    if (!('ResizeObserver' in window)) return;

    this.resizeObserver = new ResizeObserver(this.scheduleUpdate);

    const header = document.querySelector(this.headerSelector);
    const announcement = document.querySelector(this.announcementSelector);
    const firstSection = this.getFirstMainSection();

    if (header) this.resizeObserver.observe(header);
    if (announcement) this.resizeObserver.observe(announcement);
    if (firstSection) this.resizeObserver.observe(firstSection);
  }

  bindEvents() {
    // onReady runs the first update and wires the ResizeObserver, which already
    // catches post-load height changes (e.g. images), so no extra 'load' update.
    document.addEventListener('DOMContentLoaded', this.onReady);
    window.addEventListener('resize', this.scheduleUpdate);
  }

  onReady = () => {
    this.update();
    this.bindObservers();
  };

  init() {
    if (!this.gradient) return this;

    const ratioAttr = parseFloat(
      this.gradient.dataset.firstSectionRatio
    );
    if (!Number.isNaN(ratioAttr)) {
      this.firstSectionRatio = ratioAttr;
    }

    if (document.readyState === 'loading') {
      this.bindEvents();
    } else {
      this.update();
      this.bindObservers();
      window.addEventListener('resize', this.scheduleUpdate);
    }

    return this;
  }

  destroy() {
    document.removeEventListener('DOMContentLoaded', this.onReady);
    window.removeEventListener('load', this.update);
    window.removeEventListener('resize', this.scheduleUpdate);
    window.clearTimeout(this.resizeTimer);
    if (this.rafId) window.cancelAnimationFrame(this.rafId);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }
}

function initLayoutRadialGradient() {
  const gradient = document.querySelector('[data-layout-radial-gradient]');
  if (!gradient) return;

  new LayoutRadialGradient().init();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initLayoutRadialGradient);
} else {
  initLayoutRadialGradient();
}

if (typeof window !== 'undefined') {
  window.LayoutRadialGradient = LayoutRadialGradient;
}
